import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../ThemeContext';
import {
  Footprints, ListChecks, Sofa, Palette, ListTodo, FileText, ClipboardList, Ruler,
  Paintbrush, FlaskConical, Image as ImageIcon, Wrench, Calendar as CalIcon, Zap,
  DollarSign, Truck, BarChart3, FileDown, Users, Sun, Moon, Archive, ChevronDown,
} from 'lucide-react';

const GOLD_GRAD = 'linear-gradient(90deg, #C9A05E 0%, #E7C98C 50%, #C9A05E 100%)';
const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const ProjectTopMenu = ({ projectId, activeTab = '' }) => {
  const location = useLocation();
  const { theme, toggle } = useTheme();
  const dark = theme !== 'light';
  const pid = projectId;
  const tab = (n) => `/project/${pid}?tab=${encodeURIComponent(n)}`;
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  // The nav is FIXED to the viewport top (so it stays put while scrolling the
  // wide FF&E / Checklist grids, both vertically AND horizontally). A spacer of
  // the nav's live height keeps the page content from hiding under it.
  const navRef = useRef(null);
  const [spacerH, setSpacerH] = useState(56);
  useEffect(() => {
    const el = navRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setSpacerH(el.offsetHeight));
    ro.observe(el);
    setSpacerH(el.offsetHeight);
    return () => ro.disconnect();
  }, []);
  const API = (window.ENV?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin) + '/api';

  // Close the "More" popover on outside click or when the route changes.
  useEffect(() => { setMoreOpen(false); }, [location.search]);
  useEffect(() => {
    const onDoc = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const closeOutFile = async () => {
    if (!window.confirm('Close out this file?\n\nYour work is saved automatically. The client will be moved to INACTIVE (hidden from the dashboard) — you can reactivate it any time from All Projects → Show Inactive.')) return;
    try {
      await fetch(`${API}/projects/${pid}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: false }),
      });
    } catch (e) { /* still navigate home */ }
    navigate('/');
  };

  const C = dark
    ? { card: '#15130F', chip: '#34302A', text: '#EDE7DC', border: 'rgba(212,165,116,0.6)', soft: 'rgba(212,165,116,0.38)', gold: '#D4A574', onGold: '#1A1206', muted: '#9A9285' }
    : { card: '#FFFFFF', chip: '#F4F1EA', text: '#1A1611', border: 'rgba(139,115,85,0.75)', soft: 'rgba(139,115,85,0.38)', gold: '#B8894C', onGold: '#1A1206', muted: '#6B6255' };

  const PINNED = [
    { label: 'Walkthrough', icon: Footprints, target: 'Walkthrough' },
    { label: 'Checklist', icon: ListChecks, target: 'Checklist' },
    { label: 'FF&E', icon: Sofa, target: 'FF&E' },
    { label: 'Whole Home Finishes', icon: Palette, target: 'Whole Home Finishes' },
    { label: 'Tasks', icon: ListTodo, target: 'To Do' },
    { label: 'Trade Sheets', icon: FileText, target: 'Trade Sheets' },
  ];
  const PHASES = [
    { label: 'Discovery', items: [
      { label: 'Questionnaire', icon: ClipboardList, target: 'Questionnaire' },
      { label: 'Measurements', icon: Ruler, target: 'Measurements' },
    ]},
    { label: 'Selections', items: [
      { label: 'Room Finishes', icon: Paintbrush, target: 'Room Finishes' },
      { label: 'Samples', icon: FlaskConical, target: 'Samples' },
    ]},
    { label: 'Design', items: [
      { label: 'Design', icon: ImageIcon, target: 'Design' },
      { label: 'Design Tools', icon: Wrench, target: 'Design Tools' },
    ]},
    { label: 'Manage', items: [
      { label: 'Calendar', icon: CalIcon, target: 'Calendar' },
      { label: 'Automation', icon: Zap, target: 'Automation' },
    ]},
    { label: 'Procurement', items: [
      { label: 'Financial', icon: DollarSign, target: 'Finance' },
      { label: 'Logistics', icon: Truck, target: 'Shipping' },
    ]},
    { label: 'Output', items: [
      { label: 'Reports', icon: BarChart3, target: 'Reports' },
      { label: 'Exports', icon: FileDown, target: 'Exports' },
    ]},
    { label: 'Team', items: [
      { label: 'People', icon: Users, target: 'Contacts' },
    ]},
  ];

  const curTab = new URLSearchParams(location.search).get('tab') || '';
  const isActive = (item) => (curTab ? curTab === item.target : norm(activeTab) === norm(item.target));

  const pinnedBtn = (m) => {
    const on = isActive(m);
    return (
      <Link key={m.label} to={tab(m.target)} data-testid={`nav-${m.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
        className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-semibold no-underline transition-all whitespace-nowrap"
        style={on ? { background: GOLD_GRAD, color: C.onGold } : { background: C.chip, color: C.text, border: `1px solid ${C.soft}` }}>
        <m.icon size={15} /> {m.label}
      </Link>
    );
  };
  const phaseBtn = (m) => {
    const on = isActive(m);
    return (
      <Link key={m.label} to={tab(m.target)} onClick={() => setMoreOpen(false)} data-testid={`nav-${m.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
        className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-medium no-underline transition-all whitespace-nowrap"
        style={on ? { background: C.gold, color: C.onGold, border: `1px solid ${C.gold}` } : { background: C.chip, color: C.text, border: `1px solid ${C.soft}` }}>
        <m.icon size={14} style={{ color: on ? C.onGold : C.gold }} /> {m.label}
      </Link>
    );
  };

  return (
    <>
    <nav ref={navRef} className="w-full fixed top-0 left-0 right-0 z-50 print:hidden" data-testid="project-top-menu">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2"
        style={{ background: C.card, borderBottom: `1.5px solid ${C.border}`, boxShadow: dark ? '0 6px 20px rgba(0,0,0,0.55)' : '0 4px 14px rgba(60,45,25,0.12)', backdropFilter: 'blur(6px)' }}>
        {PINNED.map(pinnedBtn)}

        {/* Everything else, tucked behind a compact dropdown so the bar stays short */}
        <div className="relative" ref={moreRef}>
          <button onClick={() => setMoreOpen((o) => !o)} data-testid="nav-more-btn"
            className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-[13px] font-semibold transition-all whitespace-nowrap"
            style={{ background: moreOpen ? C.gold : C.chip, color: moreOpen ? C.onGold : C.text, border: `1px solid ${C.soft}` }}>
            More <ChevronDown size={14} style={{ transform: moreOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
          </button>
          {moreOpen && (
            <div data-testid="nav-more-menu" className="absolute left-0 mt-2 z-50 rounded-xl p-3 space-y-2"
              style={{ background: C.card, border: `1.5px solid ${C.border}`, boxShadow: '0 12px 34px rgba(0,0,0,0.45)', minWidth: 320 }}>
              {PHASES.map((ph) => (
                <div key={ph.label} className="flex flex-wrap items-center gap-2">
                  <span className="w-24 shrink-0 text-[10px] font-bold uppercase tracking-widest" style={{ color: C.gold }}>{ph.label}</span>
                  {ph.items.map(phaseBtn)}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <button onClick={closeOutFile} data-testid="close-out-file-btn" title="Close out this file — saves and moves the client to Inactive (hidden, not deleted)"
            className="flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-all hover:opacity-90"
            style={{ background: C.chip, color: C.text, border: `1px solid ${C.soft}` }}>
            <Archive size={13} style={{ color: C.gold }} /> Close out
          </button>
          <button onClick={toggle} data-testid="theme-toggle-button" title="Toggle light / dark"
            className="flex items-center gap-1 rounded-full p-1" style={{ background: C.chip, border: `1px solid ${C.soft}` }}>
            <span className="grid h-6 w-6 place-items-center rounded-full" style={{ background: !dark ? GOLD_GRAD : 'transparent', color: !dark ? C.onGold : C.muted }}><Sun size={13} /></span>
            <span className="grid h-6 w-6 place-items-center rounded-full" style={{ background: dark ? GOLD_GRAD : 'transparent', color: dark ? C.onGold : C.muted }}><Moon size={13} /></span>
          </button>
        </div>
      </div>
    </nav>
    <div aria-hidden style={{ height: spacerH }} className="print:hidden" />
    </>
  );
};

export default ProjectTopMenu;
